/**
 * Chave de atividade — `SIS-123`.
 *
 * É o fio de rastreabilidade do sistema inteiro: aparece no card, no chat
 * (`#SIS-123`), no nome da branch, no título do PR e no comentário que o Hub
 * publica no GitHub.
 *
 * Decisões: Q28 (vínculo PR↔atividade) e §2 do plano de desenvolvimento.
 */

export const ACTIVITY_KEY_PREFIX = 'SIS'

/** `SIS-123` — prefixo fixo, número inteiro positivo sem zeros à esquerda. */
const KEY_PATTERN = /^SIS-([1-9]\d*)$/

/**
 * Reconhece a chave dentro de um texto maior.
 * Exige fronteira à esquerda para não casar com `XSIS-1`, e fronteira à
 * direita para não casar com `SIS-12` dentro de `SIS-123`.
 */
const KEY_IN_TEXT = /(?<![A-Za-z0-9])SIS-([1-9]\d*)(?![0-9])/g

export type ActivityKey = `${typeof ACTIVITY_KEY_PREFIX}-${number}`

export function formatActivityKey(sequence: number): ActivityKey {
  if (!Number.isInteger(sequence) || sequence < 1) {
    throw new RangeError(
      `Sequência de atividade deve ser um inteiro >= 1, recebido: ${sequence}`,
    )
  }
  return `${ACTIVITY_KEY_PREFIX}-${sequence}`
}

export function isActivityKey(value: string): value is ActivityKey {
  return KEY_PATTERN.test(value)
}

/** Extrai o número da chave, ou `null` se o texto não for uma chave exata. */
export function parseActivityKey(value: string): number | null {
  const match = KEY_PATTERN.exec(value.trim())
  if (!match?.[1]) return null
  return Number.parseInt(match[1], 10)
}

/**
 * Encontra todas as chaves citadas em um texto livre — mensagem de chat,
 * corpo de PR, mensagem de commit.
 *
 * Case-sensitive por desenho: `sis-1` em prosa não vira vínculo acidental.
 * Retorna chaves únicas, na ordem de aparição.
 */
export function extractActivityKeys(text: string): ActivityKey[] {
  const found = new Set<ActivityKey>()
  for (const match of text.matchAll(KEY_IN_TEXT)) {
    const sequence = match[1]
    if (sequence) found.add(`${ACTIVITY_KEY_PREFIX}-${Number.parseInt(sequence, 10)}`)
  }
  return [...found]
}

/**
 * Resolve o vínculo PR↔atividade a partir dos sinais do GitHub, na ordem de
 * confiabilidade definida em docs/SDD/10-modulo-devops-github.md §5:
 * convenção de branch primeiro, depois título, depois corpo.
 *
 * Retorna a primeira chave encontrada. Um PR resolve para uma atividade;
 * citações adicionais no corpo são referências, não o vínculo.
 */
export function resolveActivityKeyFromPullRequest(input: {
  branchName?: string | undefined
  title?: string | undefined
  body?: string | undefined
}): ActivityKey | null {
  for (const source of [input.branchName, input.title, input.body]) {
    if (!source) continue
    const [first] = extractActivityKeys(source)
    if (first) return first
  }
  return null
}

/** `SIS-123-corrige-upload` — nome de branch sugerido pela UI. */
export function suggestBranchName(key: ActivityKey, title: string): string {
  const slug = title
    .normalize('NFD')
    // Remove marcas diacríticas combinantes (U+0300–U+036F): "Correção" -> "Correcao"
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '')

  return slug ? `${key}-${slug}` : key
}
